/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Label } from 'semantic-ui-react';
import regions from './regions';
import cat from './cat';
import dog from './dog';
import './style.scss';
import '../ExamSelection/style.css';
import { RestrictedSpeciesType, SPECIES, SpeciesType } from 'app/constants/species';

type RegionName =
  | 'crane'
  | 'rachis'
  | 'membre_thoracique_gauche'
  | 'membre_thoracique_droit'
  | 'thorax'
  | 'abdomen'
  | 'membre_pelvien_gauche'
  | 'membre_pelvien_droit'
  | 'tail';

interface RegionSelectionProps {
  specie: SpeciesType;
  regionSelected: string | null;
  onRegionSelection: (region: string | null) => void;
}

const rabbit = (
  <svg>
    <path d={regions.at(-1).rabbit} stroke="#77ACF1" strokeWidth="15" />
  </svg>
);
const ferret = (
  <svg>
    <path d={regions.at(-1).ferret} stroke="#77ACF1" strokeWidth="15" />
  </svg>
);
const bird = (
  <svg>
    <path d={regions.at(-1).bird} stroke="#77ACF1" strokeWidth="15" />
  </svg>
);
const reptile = (
  <svg>
    <path d={regions.at(-1).reptile} stroke="#77ACF1" strokeWidth="15" />
  </svg>
);
const rodent = (
  <svg>
    <path d={regions.at(-1).rodent} stroke="#77ACF1" strokeWidth="15" />
  </svg>
);

export const MAIN_REGIONS: RegionName[] = [
  'crane',
  'rachis',
  'membre_thoracique_gauche',
  'membre_thoracique_droit',
  'thorax',
  'abdomen',
  'membre_pelvien_gauche',
  'membre_pelvien_droit',
  'tail',
];

export function getDisplayableSpecie(
  currentSpecie: SpeciesType
): Exclude<RestrictedSpeciesType, 'other'> {
  return (SPECIES as readonly string[]).includes(currentSpecie) && currentSpecie !== 'other'
    ? (currentSpecie as Exclude<RestrictedSpeciesType, 'other'>)
    : 'dog';
}

function getAvailableSvgRegions(currentSpecie: SpeciesType): string[] {
  const displayedSpecie = getDisplayableSpecie(currentSpecie);
  return regions.filter((region) => region[displayedSpecie]).map((region) => region.name);
}

function getMissingRegions(currentSpecie: SpeciesType): RegionName[] {
  const availableRegions = getAvailableSvgRegions(currentSpecie);
  return MAIN_REGIONS.filter((region) => !availableRegions.includes(region));
}

const RegionSelection: React.FC<RegionSelectionProps> = ({
  specie,
  regionSelected,
  onRegionSelection,
}) => {
  useEffect(() => {
    return () => {
      onRegionSelection(null);
    };
  }, []);

  const getSelectedClassName = useCallback(
    (region: string): string => {
      return region === regionSelected ? 'selected' : 'not-selected';
    },
    [regionSelected]
  );

  const renderMissingRegionsList = (missingRegions: RegionName[]) => {
    return (
      <div className="missing-regions-list">
        {missingRegions.map((region) => (
          <button
            className="item"
            key={region}
            onClick={() => onRegionSelection(region)}
            type="button"
          >
            <Label id={region} className={getSelectedClassName(region)}>
              <FormattedMessage id={`exam.body_part.${region}`} />
            </Label>
          </button>
        ))}
      </div>
    );
  };

  const displayedSpecie = getDisplayableSpecie(specie);
  const missingRegions = getMissingRegions(specie);
  const hasMissingRegions = missingRegions.length > 0;

  return (
    <div className="region-selection">
      <div className="region-name-container">
        {regionSelected && (
          <p className="region-name">
            <FormattedMessage id={`exam.body_part.${regionSelected}`} />
          </p>
        )}
      </div>
      <svg viewBox="0 0 1504 1009" xmlns="http://www.w3.org/2000/svg" className="animal-body">
        {regions.map(
          (region) =>
            region[displayedSpecie] && (
              <path
                id={region.name}
                onClick={() => onRegionSelection(region.name)}
                className={getSelectedClassName(region.name)}
                d={region[displayedSpecie]}
                key={region.name}
              />
            )
        )}
        {displayedSpecie === 'cat' && cat}
        {displayedSpecie === 'dog' && dog}
        {displayedSpecie === 'rabbit' && rabbit}
        {displayedSpecie === 'ferret' && ferret}
        {displayedSpecie === 'bird' && bird}
        {displayedSpecie === 'reptile' && reptile}
        {displayedSpecie === 'rodent' && rodent}
      </svg>
      {hasMissingRegions && renderMissingRegionsList(missingRegions)}
    </div>
  );
};

export default RegionSelection;
